import {
  PotentialAnswerType,
  QuestionType,
  SelectedAnswersType,
} from '@data/types';
import useQuestionnaireStore from '@store/questionnaire';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import {
  disableButton,
  nextButtonOnclick,
  nextButtonText,
} from 'utilities/buttons';
import { hash } from 'utilities/helper';

import Answer from './Answer';
import Button from './Button';
import Prose from './Prose';
import QuestionMessage from './QuestionMessage';
import QuestionText from './QuestionText';

interface Props extends QuestionType {
  as?: keyof JSX.IntrinsicElements;
}

const Question: React.FC<Props> = ({
  as: Component = 'li',
  htmlContent = '',
  length = 1,
  index = 0,
  question,
  answers,
  pillarIndex = 0,
  answerLabels,
}) => {
  const {
    questionIndexStore,
    setQuestionIndexStore,
    setDisplayResultsStore,
    setAnswerStore,
    pillarsStore,
    displayResultsStore,
  } = useQuestionnaireStore();

  const [selectedAnswers, setSelectedAnswers] = useState<SelectedAnswersType[]>(
    []
  );

  //// Next Button Functionality
  const isLastQuestion: boolean = index + 1 === length;

  // Transform answers before sending to button functions
  const transformedAnswers: PotentialAnswerType[] = answers.map((answer) => ({
    type: answer?.type || '',
    options: answer?.options || [],
  }));

  //// Load in previous selected answers - if applicable
  // This checks local storage
  useEffect(() => {
    const pillar = pillarsStore?.[pillarIndex];
    const answers = pillar?.answers?.[index];

    if (answers) {
      setSelectedAnswers([...answers.value]);
    }
  }, []);

  return (
    <Component
      className={classNames(
        'w-full h-full md:!flex flex-row md:absolute md:inset-0 md:transition-top duration-300 text-blue',
        [
          index !== questionIndexStore ? 'md:!top-[100vh]' : 'md:!top-0',
          index === questionIndexStore ? 'flex' : 'hidden',
          displayResultsStore ? 'hidden' : '',
        ]
      )}
    >
      <div className="flex flex-col md:flex-row w-full h-full">
        <div className="w-full md:w-1/2 relative">&nbsp;</div>
        <div className="w-full md:w-1/2 bg-grey flex flex-col relative z-20 overflow-y-scroll overflow-x-hidden">
          <span className="bg-grey absolute top-0 right-[-100vw] bottom-0 left-0">
            &nbsp;
          </span>
          <div className="px-4 py-8 md:px-10 lg:py-16 lg:px-12 flex flex-col">
            <div className="relative flex flex-col h-full">
              <div className="bg-grey-dark/[0.40] p-6 rounded-tl-[2rem] rounded-br-[2rem]">
                <Prose htmlContent={htmlContent} style="style-2" />
              </div>
              <em className="uppercase block not-italic mt-16	mb-4 text-xs font-lightNormal">
                Q{index + 1} of {length}
              </em>
              <QuestionText text={question} />

              <QuestionMessage answers={transformedAnswers} />

              <div className="relative">
                <div className="overflow-hidden mb-4">
                  {answerLabels && (
                    <ul className="p-4 flex-start text-xs hidden xl:flex">
                      <li className="hidden lg:block w-2/5 pr-4">&nbsp;</li>
                      {answerLabels.map((item, index) => (
                        <li
                          key={hash([item, index])}
                          className=" w-1/3 xl:w-1/5 relative px-4 font-lightNormal leading-snug"
                        >
                          {item.value}
                          <span
                            className={classNames(
                              'border-dashed border-grey-dark border w-[1px] absolute -top-[1000px] left-0 -bottom-[1000px] block',
                              [index === 0 ? 'hidden xl:block' : '']
                            )}
                          >
                            &nbsp;
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <ul className="flex gap-4 flex-wrap">
                    {answers.map((item, answerIndex) => {
                      const isDragSingle = item.type === 'dragSingle';
                      const isRadial = item.type === 'radial';
                      const isMultiSelect = item.type === 'multiSelect';

                      return (
                        <li
                          className={classNames('', {
                            'w-full': answers.length > 2,
                            'flex-grow relative': isDragSingle,
                            'border-b border-dashed border-grey-dark last-of-type:mb-0 mb-2 pb-6 xl:mb-0 xl:pb-0 xl:border-none':
                              isRadial,
                            '-mb-4': isMultiSelect,
                          })}
                          key={hash([item, answerIndex])}
                        >
                          {isRadial && (
                            <>
                              {/* Mobile Parent Label */}
                              <strong className="text-sm font-lightNormal leading-snug block xl:hidden mb-2">
                                {item.text}
                              </strong>
                            </>
                          )}
                          <div
                            className={classNames('', {
                              'overflow-hidden mt-2': isRadial,
                            })}
                          >
                            {isRadial && (
                              <>
                                {/* Mobile Labels */}
                                {answerLabels && (
                                  <ul className="flex py-4 flex-start text-xs xl:hidden -mx-4">
                                    <li className="hidden xl:block w-2/5 pr-4">
                                      &nbsp;
                                    </li>
                                    {answerLabels.map((item, index) => (
                                      <li
                                        key={hash([item, index])}
                                        className="w-1/3 xl:w-1/5 relative font-lightNormal leading-snug px-4"
                                      >
                                        {item.value}
                                        <span
                                          className={classNames(
                                            'border-dashed border-grey-dark border w-[1px] absolute -top-[1000px] left-0 -bottom-[1000px] block',
                                            [
                                              index === 0
                                                ? 'hidden xl:block'
                                                : '',
                                            ]
                                          )}
                                        >
                                          &nbsp;
                                        </span>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </>
                            )}

                            <Answer
                              {...item}
                              questionIndex={index}
                              answerIndex={answerIndex}
                              questionText={question}
                              setSelectedAnswers={setSelectedAnswers}
                              selectedAnswers={selectedAnswers}
                              showLabels={isDragSingle}
                            />
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
              <div className="mt-auto">
                <Button
                  type="button"
                  title={nextButtonText(isLastQuestion)}
                  onClick={() => {
                    nextButtonOnclick(
                      isLastQuestion,
                      setDisplayResultsStore,
                      setQuestionIndexStore,
                      setAnswerStore,
                      index,
                      pillarIndex,
                      questionIndexStore,
                      selectedAnswers,
                      displayResultsStore
                    );
                  }}
                  disabled={disableButton(transformedAnswers, selectedAnswers)}
                  className={classNames('!bg-red mt-4 mb-8 !text-white', {
                    'opacity-50': disableButton(
                      transformedAnswers,
                      selectedAnswers
                    ),
                  })}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Component>
  );
};

export default Question;
